(() => {
  'use strict';
  const P = globalThis.HouseholdPlanner;
  const PRODUCT = 8639414862007;
  const subscription = item => Boolean(item.selling_plan_allocation);
  function offerFor(cart, variants) {
    if (cart.currency !== 'SGD') return null;
    const items = cart.items.filter(item => Number(item.product_id) === PRODUCT);
    const subscriptions = cart.items.filter(subscription);
    const once = items.filter(item => !subscription(item));
    if (once.reduce((sum, item) => sum + item.quantity, 0) > 3 || subscriptions.length > 1) return null;
    const contexts = items.map(item => String(item.properties?._garrnish_sizes || ''));
    if (!contexts.length || contexts.some(size => !Object.hasOwn(P.DOSES, size)) || new Set(contexts).size > 1) return null;
    const item = subscriptions.length ? items.find(subscription) : once.length === 1 ? once[0] : null;
    if (!item) return null;
    const size = item.properties._garrnish_sizes;
    const plans = P.recommend(P.countsFromSizes([size]));
    const basicConversion = !subscription(item) && item.quantity <= plans.alternative.boxes;
    let row;
    if (subscription(item)) {
      if (!P.validLine(item) || item.quantity !== plans.alternative.boxes || Number(item.properties._garrnish_weeks) !== plans.alternative.weeks) return null;
      row = plans.recommended;
    } else row = basicConversion ? plans.alternative : plans.recommended;
    const variant = variants.find(variant => Number(variant.id) === Number(item.variant_id));
    if (!variant?.available || !P.configurePrice(variant.price) || row.boxes < variant.minimum || (variant.maximum && row.boxes > variant.maximum) || row.boxes % variant.increment) return null;
    const allocation = variant.plans.find(plan => Number(plan.selling_plan?.id) === P.planId(row));
    if (!P.validAllocation(allocation,row)) return null;
    const price = {...P.subscriptionPrice(row,allocation),compareUnit:variant.price};
    row = {...row,unit:price.unit,subtotal:price.subtotal,total:price.subtotal};
    return {key:item.key, variantId:item.variant_id, size, row, price, basicConversion, wasSubscription:subscription(item), currentUnit:item.final_line_price/item.quantity, oldQuantity:item.quantity, sellingPlan:P.planId(row)};
  }
  function deliveryCopy(state) {
    const cart=state.cart, variants=state.variants;
    const cash=cents=>P.money(cents).replace(/\.00$/, '');
    if (!cart?.items?.length || cart.currency!=='SGD') return 'Delivery calculated at checkout';
    if (cart.items.some(subscription)) {
      const comparison=cart.items.reduce((total,item)=>{
        const variant=variants.find(v=>Number(v.id)===Number(item.variant_id));
        return total+(Number(item.product_id)===PRODUCT && variant ? variant.price*item.quantity : item.final_line_price);
      },0);
      const savings=Math.max(0,comparison-cart.total_price);
      return `You saved ${cash(savings)} and unlocked FREE delivery`;
    }
    const fallback=`Delivery: ${cash(P.PRICE.shipping)}. Choose a subscription plan for savings and FREE delivery`;
    const eligible=cart.items.filter(item=>Number(item.product_id)===PRODUCT);
    // Do not aggregate separate lines into an unverified quantity entitlement.
    if(eligible.length!==1)return fallback;
    const item=eligible[0], quantity=item.quantity;
    const variant=variants.find(v=>Number(v.id)===Number(item.variant_id));
    if(!variant?.available || !Number.isSafeInteger(quantity) || quantity<1 || !P.configurePrice(variant.price))return fallback;
    const allocations=variant.plans.filter(a=>P.validAllocation(a,{boxes:quantity,weeks:P.planWeeks(a.selling_plan?.id)}));
    const prices=[...new Set(allocations.map(a=>a.price))];
    // Cadence-independent pricing must be evidenced by the available allocations.
    if(prices.length!==1)return fallback;
    const saving=Math.max(0,(variant.price-prices[0])*quantity);
    const fullPrice=cart.items.every(item=>{
      const v=variants.find(v=>Number(v.id)===Number(item.variant_id));
      return v && item.final_line_price===v.price*item.quantity;
    }) && !(cart.cart_level_discount_applications||[]).some(d=>d.target_type==='line_item' && d.total_allocated_amount>0);
    return fullPrice
      ? `You're paying full price + ${cash(P.PRICE.shipping)} delivery. Subscribe and save ${cash(saving)} and FREE delivery`
      : `Subscribe and save ${cash(saving)} against one-time pricing and get FREE delivery`;
  }
  P.drawerDeliveryCopy=deliveryCopy;
  const signature = offer => offer && JSON.stringify([offer.key,offer.sellingPlan,offer.oldQuantity,offer.row.boxes,offer.price.subtotal]);
  P.cartOffer = offerFor;
  P.cartOfferSignature = signature;
  if (!globalThis.customElements) return;
  const escape = value => String(value).replace(/[&<>"']/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character]));
  class CartOffer extends HTMLElement {
    connectedCallback() {
      this.state = JSON.parse(this.querySelector('[data-cart-offer-state]').textContent);
      const tracker=this.closest('cart-drawer')?.querySelector('[data-delivery-tracker]');
      if(tracker)tracker.textContent=deliveryCopy(this.state);
      this.offer = offerFor(this.state.cart, this.state.variants);
      if (!this.offer) { this.hidden = true; return; }
      this.render();
      this.querySelector('button').addEventListener('click', () => this.upgrade());
    }
    t(key, values = {}) {
      return String(this.state.labels[key] || key).replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}|\[(\w+)\]/g,(match,a,b,c)=>Object.hasOwn(values,a||b||c)?String(values[a||b||c]):match);
    }
    boxes(count) { return this.t(count === 1 ? 'box' : 'boxes', {count}); }
    buttonLabel() {
      if(!this.offer.wasSubscription)return `SUBSCRIBE & SAVE ${this.offer.price.saving}% + FREE DELIVERY`;
      if(this.offer.price.volume)return 'UPGRADE & SAVE '+P.money(Math.round((this.offer.currentUnit-this.offer.price.unit)*this.offer.row.boxes)).replace(/\.00$/,'')+' MORE';
      return this.offer.wasSubscription?this.t('standard_switch'):this.t('subscribe_basic');
    }
    render() {
      const o=this.offer, compact=o.basicConversion;
      const values={weeks:o.row.weeks, boxes:this.boxes(o.row.boxes), saving:P.money((P.PRICE.once-o.price.unit)*o.row.boxes).replace('.00','')};
      const button=`<button id="CartOffer-Upgrade" class="gc-offer__button" data-subscribe="${!o.wasSubscription}" type="button" tabindex="${this.closest('cart-drawer')?.isOpen?'0':'-1'}">${escape(this.buttonLabel())}</button>`;
      const error='<p class="gc-offer__error" role="alert" hidden></p>';
      if(compact) {
        this.querySelector('[data-cart-offer-content]').innerHTML=`<section class="gc-offer gc-offer--compact">${button}${error}</section>`;
        return;
      }
      const bullets=[`Maximum savings of ${o.price.saving}% off every order`, 'FREE delivery on every order', 'Recommended timeframe for best results'];
      this.querySelector('[data-cart-offer-content]').innerHTML=`<section class="gc-offer gc-offer--large" aria-labelledby="CartOffer-Title">
        <h3 id="CartOffer-Title" tabindex="-1">${escape(this.t('upgrade_heading',values))}</h3>
        <p class="gc-offer__cadence">${escape(this.t('delivery',values))}</p>
        <ul>${bullets.map(text=>`<li><span class="gc-offer__check" aria-hidden="true">✓</span>${escape(text)}</li>`).join('')}</ul>
        <p class="gc-offer__total"><span class="gc-offer__unit"><strong>${escape(P.money(Math.round(o.price.subtotal*4/o.row.weeks)))}</strong><span>/ mo</span></span><span>${escape(P.money(o.price.subtotal))} every ${escape(o.row.weeks)} weeks</span></p>
        ${button}
        ${error}
      </section>`;
    }
    async upgrade() {
      if (window.GarrnishCart.busy) return;
      const button=this.querySelector('button');
      button.disabled=true; button.setAttribute('aria-busy','true');button.textContent=this.t('busy');
      try {
        await window.GarrnishCart.upgrade(this.offer,this.state);
        document.getElementById('CartOffer-Title')?.focus();
      } catch(error) {
        const active=document.querySelector('garrnish-cart-offer');
        const message=active?.querySelector('.gc-offer__error')||document.getElementById('CartDrawer-CartErrors');
        if(message){message.hidden=false;message.textContent=error.message;}
      } finally {
        if(this.isConnected){button.disabled=false;button.removeAttribute('aria-busy');button.textContent=this.buttonLabel();}
      }
    }
  }
  customElements.define('garrnish-cart-offer',CartOffer);
})();
